import React from "react";
import { motion, useReducedMotion, Variants } from "framer-motion";

type Direction = "up" | "down" | "left" | "right" | "none";

export interface RevealProps {
  children: React.ReactNode;
  /** Slide direction of the reveal. */
  direction?: Direction;
  /** Delay in seconds before the reveal starts. */
  delay?: number;
  /** Travel distance in px. */
  distance?: number;
  className?: string;
  /** Re-trigger every time it scrolls into view (default: once). */
  repeat?: boolean;
  as?: React.ElementType;
}

/**
 * One motion component per tag, cached forever.
 *
 * `motion.create()` mints a brand-new component type on every call, so calling
 * it during render hands React a different element type each time: it unmounts
 * the old subtree and mounts a fresh one, which replays `initial` -> the
 * content visibly fades and slides in again on every parent state change. A
 * header above an autosaving editor flickers on every keystroke. The proxy
 * properties (motion.div and friends) are already cached, so "div" is seeded
 * from there rather than created a second time.
 */
const motionTags = new Map<React.ElementType, React.ElementType>([
  ["div", motion.div],
]);

function motionTag(as: React.ElementType): React.ElementType {
  const cached = motionTags.get(as);
  if (cached) return cached;
  const created = motion.create(as as React.ComponentType<any>);
  motionTags.set(as, created);
  return created;
}

function offset(direction: Direction, distance: number) {
  switch (direction) {
    case "up":
      return { y: distance };
    case "down":
      return { y: -distance };
    case "left":
      return { x: distance };
    case "right":
      return { x: -distance };
    default:
      return {};
  }
}

/**
 * Scroll-reveal wrapper. Fades + slides content into view once it enters the
 * viewport. Fully disabled under `prefers-reduced-motion`.
 */
const Reveal: React.FC<RevealProps> = ({
  children,
  direction = "up",
  delay = 0,
  distance = 22,
  className,
  repeat = false,
  as,
}) => {
  const reduce = useReducedMotion();
  const MotionTag = motionTag(as || "div");

  if (reduce) {
    const Tag = (as || "div") as React.ElementType;
    return <Tag className={className}>{children}</Tag>;
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, ...offset(direction, distance) }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: !repeat, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  );
};

/** Container that staggers the reveal of its direct <RevealItem> children. */
export const RevealGroup: React.FC<{
  children: React.ReactNode;
  className?: string;
  stagger?: number;
  repeat?: boolean;
}> = ({ children, className, stagger = 0.09, repeat = false }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;

  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: stagger } },
  };

  return (
    <motion.div
      className={className}
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: !repeat, amount: 0.15 }}
    >
      {children}
    </motion.div>
  );
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

/** Child of <RevealGroup>. */
export const RevealItem: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={itemVariants}>
      {children}
    </motion.div>
  );
};

export default Reveal;
